import React from 'react';
import { useQuery } from '@tanstack/react-query';
import { adminApiService } from '../../services/adminApi';
import { TableCard, TableHeader, TableViewport } from '../table/TableKit';
import {
  Badge,
  EmptyRow,
  ErrorNotice,
  LoadingSpinner,
  StatTile,
  formatNumber,
  tdClass,
  thClass,
} from './shared';

function formatBytes(value: number): string {
  if (value >= 1024 ** 3) return `${(value / 1024 ** 3).toFixed(1)} GB`;
  if (value >= 1024 ** 2) return `${(value / 1024 ** 2).toFixed(1)} MB`;
  if (value >= 1024) return `${(value / 1024).toFixed(1)} KB`;
  return `${value} B`;
}

function formatMetric(key: string, value: unknown): string {
  if (typeof value === 'number') {
    if (key.includes('bytes')) return formatBytes(value);
    if (key.includes('percent')) return `${value}%`;
    return formatNumber(value);
  }
  if (Array.isArray(value)) return value.join(', ');
  if (value !== null && typeof value === 'object') {
    return Object.entries(value as Record<string, unknown>)
      .map(([k, v]) => `${k}: ${formatMetric(key, v)}`)
      .join(', ');
  }
  return String(value);
}

function AdminHealth() {
  const { data, isLoading, error } = useQuery({
    queryKey: ['admin', 'health'],
    queryFn: adminApiService.getHealth,
    refetchInterval: 30_000,
    retry: false,
  });

  if (isLoading) return <LoadingSpinner />;
  if (error || !data) return <ErrorNotice message="Failed to load health metrics." />;

  // Response is flat: "status" (+ "error") alongside the metric keys.
  const metrics = Object.fromEntries(
    Object.entries(data).filter(([key]) => key !== 'status' && key !== 'error'),
  );
  const num = (key: string): number | undefined =>
    typeof metrics[key] === 'number' ? (metrics[key] as number) : undefined;

  return (
    <div>
      <div className="flex items-center justify-between mb-4">
        <h2 className="text-xl font-bold text-heading">Health</h2>
        <span className="text-xs text-secondary">Auto-refreshes every 30s</span>
      </div>

      {data.error && <ErrorNotice message={data.error} />}

      <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
        <StatTile
          label="Status"
          value={<Badge value={data.status === 'healthy' ? 'success' : 'failed'} />}
        />
        <StatTile label="Queue Depth" value={formatNumber(num('queue_depth') ?? 0)} />
        <StatTile
          label="Disk Used"
          value={num('disk_space_used_percent') != null ? `${num('disk_space_used_percent')}%` : '—'}
          sub={
            num('disk_space_available_bytes') != null
              ? `${formatBytes(num('disk_space_available_bytes')!)} free`
              : undefined
          }
        />
        <StatTile
          label="CPU"
          value={num('cpu_total_used_percent') != null ? `${num('cpu_total_used_percent')}%` : '—'}
          sub={
            num('ram_used_percent') != null ? `RAM ${num('ram_used_percent')}%` : undefined
          }
        />
      </div>

      <TableCard>
        <TableHeader title="Metrics" />
        <TableViewport>
          <table className="w-full">
            <thead>
              <tr className="border-b border-gray-700">
                <th className={thClass}>Metric</th>
                <th className={thClass}>Value</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-700">
              {Object.entries(metrics)
                .sort(([a], [b]) => a.localeCompare(b))
                .map(([key, value]) => (
                  <tr key={key}>
                    <td className={`${tdClass} font-mono`}>{key}</td>
                    <td className={tdClass}>{formatMetric(key, value)}</td>
                  </tr>
                ))}
              {Object.keys(metrics).length === 0 && <EmptyRow colSpan={2} text="No metrics available." />}
            </tbody>
          </table>
        </TableViewport>
      </TableCard>
    </div>
  );
}

export default AdminHealth;
